import { ChatBubble } from "@/components/ai/chat/ChatBubble";
import { TodoContainer } from "@/components/ai/chat/TodoContainer";
import { type AgentMessageFragment, type Message } from "@/hooks/use-stream-conversation";
import { type PlainAgentExecution } from "@/queries/agent-queries";
import { filterMessagesByType, MESSAGE_TYPES } from "@/utils/chat";
import { groupMessagesByTodo, calculateTodoGroupProgress } from "@/utils/message-grouping";
import { type FC, useMemo } from "react";

type GroupedMessagesProps = {
    messages: Message[];
    agentExecution: PlainAgentExecution;
    onClick?: (block: AgentMessageFragment) => void;
};

export const GroupedMessages: FC<GroupedMessagesProps> = ({ messages, agentExecution, onClick }) => {
    const groupedMessages = useMemo(() => {
        return groupMessagesByTodo(messages);
    }, [messages]);

    return groupedMessages.map((group, groupIndex) => {
        const isLastGroup = groupIndex === groupedMessages.length - 1;

        if (group.type === "user") {
            return (
                <div key={group.id} className="flex w-full justify-end">
                    <ChatBubble
                        agentExecution={agentExecution}
                        message={group}
                        onClick={onClick}
                        isLast={isLastGroup}
                        agentMessages={filterMessagesByType(messages, MESSAGE_TYPES.AGENT)}
                        data-tracking-id-none
                    />
                </div>
            );
        } else if (group.type === "agent") {
            return (
                <div key={group.id} className="w-full">
                    <ChatBubble
                        agentExecution={agentExecution}
                        message={group}
                        onClick={onClick}
                        isLast={isLastGroup}
                        agentMessages={filterMessagesByType(messages, MESSAGE_TYPES.AGENT)}
                        data-tracking-id-none
                    />
                </div>
            );
        } else if (group.type === "todo-group") {
            const { started, total } = calculateTodoGroupProgress(group.todos);
            return (
                <div key={group.groupId} className="flex w-full flex-col gap-2">
                    <div className="flex items-center gap-2">
                        <div className="rounded bg-surface-muted px-1.5 py-0.5 text-xs font-medium uppercase tracking-[0.5px] text-content-muted">
                            TODOs
                        </div>
                        <span className="text-sm font-medium text-content-muted">
                            {started}/{total}
                        </span>
                    </div>
                    <div className="flex flex-col overflow-clip rounded-lg border border-border-base bg-surface-pure">
                        {group.todos.map((todo, todoIndex) => {
                            const isLastInBlock = todoIndex === group.todos.length - 1;
                            return (
                                <TodoContainer
                                    key={todo.todoId}
                                    todo={todo}
                                    isLast={isLastGroup && isLastInBlock}
                                    agentExecution={agentExecution}
                                    onClick={onClick}
                                    data-tracking-id="default-tracking-id"
                                />
                            );
                        })}
                    </div>
                </div>
            );
        }
        return null;
    });
};
